import { ConnectionState, OnboardingDetailsRole } from '@nemuru-eng/js-api-client';
import React, { useContext } from 'react';

import { ApplicationParticipant, PageHighlight, SectionScheme, Widget } from '@/config/types';
import { addLoanHighlights } from '@/contexts/PageContext/actions/addLoanHighlights';
import { addRequiredWidgets } from '@/contexts/PageContext/actions/addRequiredWidgets';
import { addWidgetsToRequirements } from '@/contexts/PageContext/actions/addWidgetsToRequirements';
import { disableLoading } from '@/contexts/PageContext/actions/disableLoading';
import { makeSectionVisible } from '@/contexts/PageContext/actions/makeSectionVisible';
import { resetPage } from '@/contexts/PageContext/actions/resetPage';
import { setConstructing } from '@/contexts/PageContext/actions/setConstructing';
import { setCustomSubtitle } from '@/contexts/PageContext/actions/setCustomSubtitle';
import { setCustomTitle } from '@/contexts/PageContext/actions/setCustomTitle';
import { setLiveState } from '@/contexts/PageContext/actions/setLiveState';
import { setLoading } from '@/contexts/PageContext/actions/setLoading';
import { setLoanSourceType } from '@/contexts/PageContext/actions/setLoanSourceType';
import { setNotFound } from '@/contexts/PageContext/actions/setNotFound';
import { setPage } from '@/contexts/PageContext/actions/setPage';
import { setApplicationParticipant } from '@/contexts/PageContext/actions/setParticipantRole';
import { setShowLeftMenu } from '@/contexts/PageContext/actions/setShowLeftMenu';
import { setShowMobileLeftMenu } from '@/contexts/PageContext/actions/setShowMobileLeftMenu';
import { setShowMobilePageRightMenu } from '@/contexts/PageContext/actions/setShowMobilePageRightMenu';
import { setShowSuperSearchBar } from '@/contexts/PageContext/actions/setShowSuperSearchBar';

export const pageState = {
  sections: [] as SectionScheme[],
  sectionsVisible: [] as number[],
  widgets: [] as [] | Widget[],
  requiredWidgets: [] as [] | Widget[],
  loanSourceType: undefined as string | undefined,
  applicationParticipant: {
    role: OnboardingDetailsRole.BORROWER,
    onboardingDetails: undefined,
  } as ApplicationParticipant,
  customTitleTId: undefined as string | undefined,
  customSubtitleTId: undefined as string | undefined,
  loading: false,
  loadingDisabled: false,
  constructing: true,
  loadingFeatures: [] as string[],
  notFound: undefined as any,
  loanHighlights: [] as PageHighlight[],
  showLeftMenu: true,
  showMobileLeftMenu: false as boolean,
  showMobilePageRightMenu: false as boolean,
  showSuperSearchBar: false as boolean,
  /** Real-time stream of the page's resource, shown next to the title; 'closed' hides the indicator. */
  liveState: 'closed' as ConnectionState,
};

const actionsDefinitions = (setStateById: (id: string, stateChange: stateChangeInPageContext) => void) => ({
  setPage: setPage(setStateById),
  setCustomTitle: setCustomTitle(setStateById),
  setCustomSubtitle: setCustomSubtitle(setStateById),
  setConstructing: setConstructing(setStateById),
  setLoading: setLoading(setStateById),
  disableLoading: disableLoading(setStateById),
  addRequiredWidgets: addRequiredWidgets(setStateById),
  addWidgetsToRequirements: addWidgetsToRequirements(setStateById),
  resetPage: resetPage(setStateById),
  setNotFound: setNotFound(setStateById),
  setLoanSourceType: setLoanSourceType(setStateById),
  setApplicationParticipant: setApplicationParticipant(setStateById),
  addLoanHighlights: addLoanHighlights(setStateById),
  makeSectionVisible: makeSectionVisible(setStateById),
  setShowLeftMenu: setShowLeftMenu(setStateById),
  setShowMobileLeftMenu: setShowMobileLeftMenu(setStateById),
  setShowMobilePageRightMenu: setShowMobilePageRightMenu(setStateById),
  setShowSuperSearchBar: setShowSuperSearchBar(setStateById),
  setLiveState: setLiveState(setStateById),
});

const PageContext = React.createContext({
  state: pageState,
  actions: actionsDefinitions(() => () => {}),
});

export type action = (setState: setStateInPageContext, actionId: string) => () => void;

export type setPageStateById = (id: string, stateChange: stateChangeInPageContext) => void;
export type stateChangeInPageContext = (prevState: typeof pageState) => typeof pageState;
export type setStateInPageContext = React.Dispatch<stateChangeInPageContext>;

export const usePageContext = () => useContext(PageContext);

export function PageContextProvider(props) {
  const [state, setState] = React.useState(pageState);

  const setStateById: setPageStateById = (_, stateChange) => {
    setState(stateChange);
  };

  const actions = actionsDefinitions(setStateById);

  const showLeftMenuStored = localStorage.getItem('left_menu');
  if (showLeftMenuStored === null || showLeftMenuStored === undefined) {
    localStorage.setItem('left_menu', 'true');
  }

  const newState = {
    ...state,
    showLeftMenu: localStorage.getItem('left_menu') === 'true',
  };

  return <PageContext.Provider value={{ state: newState, actions }}>{props.children}</PageContext.Provider>;
}
